/* App Tư vấn cơ sở — logic thuần: lượt tư vấn tại cơ sở từ check-in đến chốt và chuyển thực hiện (lưu trên máy, chờ nối API) */
import dayjs from 'dayjs';
import { BRANCHES, CONSULTANTS, rng } from '../Telesales/dashboard/data';
import type { Mang } from '../Telesales/lead/logic';
import { cardSummary, type QuoteCard } from '../Telesales/lead/quote';

export const MIN = 60_000;
export const HOUR = 60 * MIN;
export const DAY = 24 * HOUR;
/** Khách chờ quá N phút chưa được nhận thì tô đỏ */
export const WAIT_WARN_MIN = 15;
/** Mức giảm trong quyền Tư vấn cơ sở (%) — chưa chốt số, vượt thì xin Trưởng phòng duyệt */
export const NGUONG_TVCS = 10;
/** Trần giảm mỗi dịch vụ / combo (Chính sách giá 7.1–7.3) */
export const TRAN_DV = 30;
export const TRAN_COMBO = 40;
export const QUOTE_VALID_DAYS = 14;

export interface Doctor { id: string; name: string }
export const DOCTORS: Doctor[] = [
  { id: 'BS-01', name: 'BS. Trần Quốc Phong' },
  { id: 'BS-02', name: 'BS. Lê Minh Châu' },
  { id: 'BS-03', name: 'BS. Phạm Hải Đăng' },
  { id: 'BS-04', name: 'BS. Vũ Ngọc Hân' },
];
/** Tên nhân sự, cơ sở tải từ server (mã là id trên server); mã cũ dạng BS-01, KTV-01 vẫn đọc từ danh sách mẫu */
const staffNames = new Map<string, string>();
export const registerStaffNames = (rows: [string, string][]) => rows.forEach(([id, name]) => staffNames.set(id, name));
export const staffNameOf = (id?: string) => (id ? staffNames.get(id) : undefined);
const branchNames = new Map<string, string>();
export const registerBranchNames = (rows: [string, string][]) => rows.forEach(([id, name]) => branchNames.set(id, name));
export const branchIdByName = (name?: string | null) => [...branchNames].find(([, n]) => n === name)?.[0];
export const doctorName = (id?: string) => staffNameOf(id) ?? DOCTORS.find((d) => d.id === id)?.name ?? '—';
export const CONSULT_ROOMS = ['Phòng tư vấn 1', 'Phòng tư vấn 2', 'Phòng tư vấn 3'];
export const EXEC_ROOMS = ['Phòng thủ thuật 1', 'Phòng thủ thuật 2', 'Phòng laser', 'Phòng mổ 1'];
/** Kỹ thuật viên làm dịch vụ tại cơ sở (minh hoạ, chờ nối API nhân sự) — mảng = dịch vụ làm được */
export interface Tech { id: string; name: string; branchId: string; mangs: Mang[] }
export const TECHS: Tech[] = [
  { id: 'KTV-01', name: 'KTV Nguyễn Thị Hoa', branchId: 'q1', mangs: ['Clinic'] },
  { id: 'KTV-02', name: 'KTV Trần Văn Bảo', branchId: 'q1', mangs: ['Newhair'] },
  { id: 'KTV-03', name: 'KTV Lê Thu Hà', branchId: 'q1', mangs: ['Clinic', 'PTTM'] },
  { id: 'KTV-04', name: 'KTV Phạm Quốc Huy', branchId: 'q1', mangs: ['Newhair', 'Clinic'] },
  { id: 'KTV-05', name: 'KTV Võ Ngọc Trâm', branchId: 'q1', mangs: ['PTTM', 'Partnership'] },
];
export const techName = (id?: string) => staffNameOf(id) ?? TECHS.find((t) => t.id === id)?.name ?? id ?? '—';
export const LEVELS = ['Mức 1', 'Mức 2', 'Mức 3', 'Mức 4'];

export interface Svc { id: string; name: string; unit: string; price: number; /** Dịch vụ chủ lực: khoá giảm % */ core?: boolean }
/** Bảng giá niêm yết theo mảng — giá minh hoạ, chờ nối Công cụ báo giá */
export const CATALOG: Record<Mang, Svc[]> = {
  Newhair: [
    { id: 'nh-regen', name: 'Giải pháp Newhair · Regen phục hồi', unit: 'buổi', price: 2_500_000 },
    { id: 'nh-fue', name: 'Giải pháp Newhair · cấy tóc', unit: 'gói', price: 45_000_000, core: true },
    { id: 'nh-ai', name: 'AI Hair Analysis', unit: 'lần', price: 1_000_000 },
    { id: 'nh-home', name: 'Homecare chăm sóc da đầu', unit: 'hộp', price: 1_000_000 },
    { id: 'nh-check', name: 'Phân tích tóc định kỳ', unit: 'lần', price: 500_000 },
  ],
  Clinic: [
    { id: 'cl-klift', name: 'Phác đồ K·Lift căng chỉ', unit: 'lần', price: 8_000_000, core: true },
    { id: 'cl-reskin', name: 'Phác đồ K·Reskin', unit: 'buổi', price: 1_500_000 },
    { id: 'cl-nam', name: 'Điều trị nám · tàn nhang', unit: 'buổi', price: 1_200_000 },
    { id: 'cl-peel', name: 'Peel da chuẩn y khoa', unit: 'buổi', price: 800_000 },
    { id: 'cl-home', name: 'Homecare phục hồi da', unit: 'hộp', price: 900_000 },
  ],
  PTTM: [
    { id: 'pt-mui', name: 'Nâng mũi cấu trúc', unit: 'ca', price: 45_000_000, core: true },
    { id: 'pt-mi', name: 'Cắt mí', unit: 'ca', price: 18_000_000 },
    { id: 'pt-mo', name: 'Hút mỡ bụng', unit: 'ca', price: 40_000_000, core: true },
    { id: 'pt-kham', name: 'Khám và tư vấn bác sĩ', unit: 'lần', price: 300_000 },
    { id: 'pt-hau', name: 'Thuốc và chăm sóc hậu phẫu', unit: 'gói', price: 2_000_000 },
  ],
  Partnership: [
    { id: 'pa-goi', name: 'Gói dịch vụ đối tác', unit: 'gói', price: 10_000_000 },
    { id: 'pa-kham', name: 'Khám tổng quát đối tác', unit: 'lần', price: 500_000 },
  ],
};
export const svcById = (id?: string) => Object.values(CATALOG).flat().find((s) => s.id === id);
/** Chương trình ưu đãi đang chạy (quà tặng, không phải % giảm) — minh hoạ */
export const PROGRAMS = ['Tặng 1 buổi chăm sóc sau làm', 'Miễn phí tái khám 3 tháng', 'Tặng AI Hair Analysis'];
export const VAT_OPTS = [
  { value: 0, label: 'Chưa áp dụng' },
  { value: 8, label: '8%' },
  { value: 10, label: '10%' },
];

export type Source = 'Telesales' | 'Đặt lịch' | 'Vãng lai' | 'CSKH' | 'Khách cũ';
export type VisitSt = 'referral' | 'waiting' | 'consulting' | 'done' | 'cancelled';
export const VISIT_ST: Record<VisitSt, { label: string; color: string }> = {
  referral: { label: 'Phiếu CSKH · chưa đến', color: 'purple' },
  waiting: { label: 'Đang chờ', color: 'gold' },
  consulting: { label: 'Đang tư vấn', color: 'processing' },
  done: { label: 'Đã tư vấn', color: 'green' },
  cancelled: { label: 'Đã huỷ', color: 'default' },
};
export type StaffSt = 'ready' | 'break' | 'off';
export const STAFF_ST: Record<StaffSt, { label: string; dot: string }> = {
  ready: { label: 'Sẵn sàng', dot: '#22c55e' },
  break: { label: 'Tạm nghỉ', dot: '#f59e0b' },
  off: { label: 'Ngoại tuyến', dot: '#94a3b8' },
};

export interface LogItem { at: number; text: string; by: string }
/** items[].id = dịch vụ trong danh mục hệ thống (báo giá Telesales mới); báo giá cũ không có */
export interface OnlineQuote {
  code: string; sentAt: number; validUntil: number; items: { id?: number; name: string; price: number | null; card?: CardInfo }[]; total: number | null; by: string;
  /** Ưu đãi Telesales đã báo khách — phiếu báo giá chính thức giữ nguyên */
  promo?: string; discount?: number; reason?: string;
}
export interface OnlineDeposit { code: string; amount: number; purpose: string; status: string; received: boolean }
/** Thẻ liệu trình bán trên phiếu (cùng kiểu với báo giá online Telesales) — POS phát hành thẻ khi thu tiền */
export type CardInfo = QuoteCard;
export { cardSummary };
export interface QLine { id: string; svcId?: string; /** Mã dịch vụ trên API — đẩy giỏ sang POS */ apiId?: number; name: string; unit: string; qty: number; price: number; gift?: boolean; core?: boolean; /** Dòng là thẻ liệu trình (không phải dịch vụ lẻ) */ card?: CardInfo }
/** reqs = yêu cầu chuyên môn bác sĩ tích (mã trong REQS) — đi tiếp sang Chuyển thực hiện, không nằm trong ghi chú */
/** sign = chữ ký tay của bác sĩ (ảnh PNG data URL), bắt buộc khi ký khám */
export interface Exam { doctor?: string; level?: string; area?: string; note?: string; reqs?: string[]; signedAt?: number; sign?: string }

/** Yêu cầu chuyên môn dạng tích chọn: bộ phận chuyên môn đọc được (ghi chú tự do thì không) */
export const REQS: { key: string; label: string }[] = [
  { key: 'gay_me', label: 'Cần gây mê' },
  { key: 'gay_te', label: 'Cần gây tê' },
  { key: 'so_dau', label: 'Khách sợ đau' },
  { key: 'xn_mau', label: 'Cần xét nghiệm máu' },
  { key: 'xn_hiv', label: 'Cần xét nghiệm HIV / viêm gan' },
  { key: 'dung_cu', label: 'Dùng bộ dụng cụ riêng' },
  { key: 'huyet_ap', label: 'Theo dõi huyết áp' },
];
export const reqLabel = (k: string) => REQS.find((r) => r.key === k)?.label ?? k;
/** Vùng gợi ý theo mảng — bác sĩ chọn nhanh, vẫn gõ được vùng khác */
export const AREAS: Record<Mang, string[]> = {
  Newhair: ['Đỉnh đầu', 'Vùng trán', 'Hai bên thái dương', 'Toàn đầu'],
  Clinic: ['Toàn mặt', 'Trán', 'Hai má', 'Cằm', 'Cổ', 'Toàn thân'],
  PTTM: ['Mũi', 'Mắt', 'Ngực', 'Bụng', 'Đùi'],
  Partnership: ['Theo gói đối tác'],
};

/* ---------------- Khai báo thông tin ban đầu (khách tự điền sau check-in) ---------------- */

/**
 * Theo mẫu phiếu giấy khách gửi ("Đăng ký thẻ thành viên khách hàng"): thông tin cá nhân, bệnh sử, thực phẩm chức năng,
 * thuốc đang dùng, tình trạng cơ thể, thông tin thăm viện, người viết đơn + chữ ký
 */
export const SCR = {
  jobs: ['Học sinh', 'Nhân viên văn phòng', 'Nội trợ', 'Kinh doanh', 'Công viên chức', 'Bác sĩ', 'Nghệ sĩ', 'Khác'],
  blood: ['A', 'B', 'AB', 'O', 'Loại khác', 'Không biết'],
  // HIV / viêm gan: khách nêu ở buổi họp 30/10 (cần xét nghiệm, dụng cụ riêng) — phiếu giấy chưa có dòng này
  history: ['Huyết áp', 'Đái đường', 'Bệnh tim', 'Suy thận', 'Hen suyễn', 'Suy hô hấp', 'Khô giác mạc / trị liệu mắt khác', 'Dị ứng', 'HIV / viêm gan', 'Khác'],
  supplements: ['Nước ép hành tây', 'Cao sâm / cao hồng sâm', 'Nước ép / cao tỏi', 'Nước cất cây ngân hạnh', 'Squalene', 'Omega3', 'Thuốc cải thiện máu khác', 'Vitamin', 'Các loại thực phẩm khác'],
  meds: ['Cao huyết áp', 'Tiểu đường', 'Trợ tim', 'Aspirin', 'Chống viêm nhiễm', 'Giảm đau đặc trị', 'Hoocmon', 'Thuốc Bắc', 'Khác'],
  channels: ['Quảng cáo online', 'Trang chủ', 'Tìm kiếm trên mạng', 'Báo chí', 'Biển quảng cáo', 'Du lịch thẩm mỹ', 'Công ty giải trí', 'Nhân viên / Viện trưởng giới thiệu', 'Người quen giới thiệu'],
  // Mục "Nhận tư vấn về" của phiếu + nhóm dịch vụ chính của Khơ Thị (tóc, da)
  interests: ['Mắt', 'Mũi', 'Xương hàm mặt', 'Ngực', 'Cấy mỡ', 'Hút mỡ', 'Nâng cơ', 'Filler', 'Botox', 'Cấy tóc / tóc', 'Da (nám, mụn, trẻ hoá)', 'Triệt lông', 'Khác'],
};
/** Thực phẩm chức năng / thuốc làm loãng máu — dễ chảy máu, bầm tím khi làm thủ thuật */
const BLEED = ['Nước ép hành tây', 'Cao sâm / cao hồng sâm', 'Nước ép / cao tỏi', 'Nước cất cây ngân hạnh', 'Omega3', 'Thuốc cải thiện máu khác', 'Aspirin'];

export interface Screening {
  at: number;
  name?: string; dob?: string; gender?: 1 | 2; age?: number; address?: string; phone?: string; phone2?: string; email?: string;
  blood?: string; bloodOther?: string; married?: boolean; children?: string; job?: string;
  history: string[]; historyOther?: string; treatment?: string; surgery?: boolean; surgeryNote?: string;
  supplements: string[]; meds: string[]; medsOther?: string;
  bruise?: boolean; smoke?: boolean;
  channels: string[]; referrer?: string;
  doctor?: string; consultant?: string;
  interests: string[]; interestOther?: string;
  /** Người viết đơn: bệnh nhân hoặc người bảo hộ (quan hệ) */
  writerRelation?: string; writerName?: string;
  /** Chữ ký tay (ảnh PNG dạng data URL) */
  signature?: string;
  /** Đồng ý lưu thông tin, nhận thông tin dịch vụ qua bưu điện, tin nhắn, điện thoại, email */
  consent?: boolean;
}
export const emptyScreening = (): Screening => ({ at: 0, history: [], supplements: [], meds: [], channels: [], interests: [] });

/** Ô khai sai định dạng (server hiện không chặn): ngày sinh dd/mm/yyyy có thật, email, số điện thoại 8–15 số */
export function screeningIssues(f: Pick<Screening, 'dob' | 'email' | 'phone' | 'phone2'>, now = Date.now()): string[] {
  const out: string[] = [];
  const dob = f.dob?.trim();
  if (dob) {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dob);
    const d = m && new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    const real = !!d && d.getMonth() === Number(m![2]) - 1 && d.getDate() === Number(m![1]) && Number(m![3]) >= 1900 && d.valueOf() <= now;
    if (!real) out.push('Ngày sinh (dd/mm/yyyy)');
  }
  if (f.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) out.push('E-mail');
  const badPhone = (p?: string) => !!p?.trim() && !/^\+?\d{8,15}$/.test(p.replace(/[\s.()-]/g, ''));
  if (badPhone(f.phone)) out.push('Số điện thoại');
  if (badPhone(f.phone2)) out.push('Số điện thoại dự phòng');
  return out;
}

/** Cờ y khoa cho tư vấn viên, bác sĩ, chuyên môn */
export function screenFlags(s?: Screening): string[] {
  if (!s) return [];
  const out = s.history.filter((h) => h !== 'Khác' && h !== 'HIV / viêm gan');
  if (s.history.includes('HIV / viêm gan')) out.push('HIV / viêm gan: xét nghiệm · dụng cụ riêng');
  if (s.history.includes('Khác') && s.historyOther) out.push(s.historyOther);
  if (s.surgery) out.push('Đã từng phẫu thuật');
  const meds = s.meds.filter((m) => m !== 'Khác');
  if (meds.length) out.push(`Đang dùng thuốc: ${meds.join(', ')}`);
  if (s.bruise || [...s.supplements, ...s.meds].some((x) => BLEED.includes(x))) out.push('Nguy cơ chảy máu / bầm tím');
  if (s.smoke) out.push('Hút thuốc');
  return out;
}
/** Yêu cầu chuyên môn gợi ý cho bác sĩ từ khai báo */
export function screenReqs(s?: Screening): string[] {
  if (!s) return [];
  const out: string[] = [];
  if (s.history.includes('Huyết áp') || s.history.includes('Bệnh tim') || s.meds.includes('Cao huyết áp') || s.meds.includes('Trợ tim')) out.push('huyet_ap');
  if (s.bruise || [...s.supplements, ...s.meds].some((x) => BLEED.includes(x)) || s.history.includes('Đái đường')) out.push('xn_mau');
  if (s.history.includes('HIV / viêm gan')) out.push('xn_hiv', 'dung_cu');
  return out;
}
export interface Approval { status: 'pending' | 'approved' | 'rejected'; pct: number; at: number; by?: string; note?: string }
export interface OfficialQuote {
  code: string;
  at: number;
  by: string;
  lines: QLine[];
  program?: string;
  discount: number;
  reason?: string;
  approval?: Approval;
  vat: number;
  validUntil: number;
  exam: Exam;
  sentAt?: number;
  /** Đã chuyển giỏ sang POS (POS thu tiền, trừ cọc — không làm ở đây) */
  posAt?: number;
}
export type SiteDepSt = 'requested' | 'received' | 'cancelled';
export const SITE_DEP_ST: Record<SiteDepSt, { label: string; color: string }> = {
  requested: { label: 'Chờ quầy thu', color: 'gold' },
  received: { label: 'Đã nhận', color: 'green' },
  cancelled: { label: 'Đã huỷ', color: 'default' },
};
export const DEP_PURPOSES = ['Giữ lịch thực hiện', 'Giữ ưu đãi', 'Giữ bác sĩ'];
export const DEP_METHODS = ['Thu tại quầy', 'Chuyển khoản mã QR'];
/** planNo = khoản thu của đợt nào trong kế hoạch thanh toán (không có = cọc lẻ) */
export interface SiteDeposit { code: string; amount: number; purpose: string; method: string; status: SiteDepSt; at: number; by: string; quote?: string; receivedAt?: number; cancelReason?: string; planNo?: number }

/*
 * ---------------- Thanh toán nhiều đợt ----------------
 * Tối đa 12 lần trong 12 tháng như trả góp POS. Khác POS: lần 1 là số khách trả hôm nay, không có mức tối thiểu 30%
 * và không cần Quản lý duyệt (họp 30/10 — Kế hoạch thanh toán chỉ ghi nhận khách định trả thế nào, quầy POS thu tiền).
 */

/** due trống = chưa hẹn ngày (khách chưa nói khi nào trả — không bắt hỏi) */
export interface PayStep { no: number; due?: number; amount: number }
export const PLAN_MAX = 12;
/** Đợt cuối trong 12 tháng kể từ đợt 1 (như POS) */
export const PLAN_MAX_MONTHS = 12;
const r1000 = (n: number) => Math.round(n / 1000) * 1000;

/**
 * Đợt 1 thu hôm nay; các đợt sau cách nhau `months` tháng (months = 0: để trống ngày), phần còn lại chia đều
 * (làm tròn nghìn, đợt cuối nhận phần lẻ)
 */
export function buildPayPlan(total: number, count: number, first: number, start: number, months = 1): PayStep[] {
  const n = Math.max(1, Math.min(PLAN_MAX, Math.floor(count)));
  const day0 = dayjs(start).startOf('day');
  if (n === 1) return [{ no: 1, due: day0.valueOf(), amount: total }];
  const f = Math.min(Math.max(0, first), total);
  const each = r1000((total - f) / (n - 1));
  let left = total - f;
  return Array.from({ length: n }, (_, i) => {
    if (i === 0) return { no: 1, due: day0.valueOf(), amount: f };
    const amount = i === n - 1 ? left : Math.min(each, left);
    left -= amount;
    return { no: i + 1, ...(months ? { due: day0.add(months * i, 'month').valueOf() } : {}), amount };
  });
}

/**
 * Kiểm tra kế hoạch tư vấn viên tự chia: tổng các đợt khớp số phải thu, mỗi đợt > 0, ngày tăng dần,
 * đợt chưa thu không hẹn trước hôm nay, tối đa 12 đợt. locked = số đợt đã thu / đã gửi quầy (không sửa được).
 */
export function planIssues(steps: PayStep[], target: number, now: number, locked: number[] = []): string[] {
  const out: string[] = [];
  if (!steps.length) return ['Chưa có đợt nào.'];
  if (steps.length > PLAN_MAX) out.push(`Tối đa ${PLAN_MAX} đợt.`);
  const sum = steps.reduce((s, x) => s + x.amount, 0);
  if (sum !== target) out.push(sum < target ? `Tổng các đợt còn thiếu ${vnd(target - sum)} so với số phải thu.` : `Tổng các đợt vượt ${vnd(sum - target)} so với số phải thu.`);
  if (steps.some((s) => !(s.amount > 0))) out.push('Mỗi đợt phải lớn hơn 0.');
  // Ngày là tuỳ chọn: chỉ kiểm tra các đợt đã hẹn ngày
  const dated = steps.filter((s) => s.due != null);
  if (dated.some((s, i) => i > 0 && !dayjs(s.due).isAfter(dated[i - 1].due, 'day'))) out.push('Ngày các đợt phải tăng dần, không trùng ngày.');
  if (dated.some((s) => !locked.includes(s.no) && dayjs(s.due).isBefore(now, 'day'))) out.push('Đợt chưa thu không được hẹn trước hôm nay.');
  const start = steps[0].due ?? now;
  if (dated.length && dayjs(dated[dated.length - 1].due).isAfter(dayjs(start).add(PLAN_MAX_MONTHS, 'month'), 'day')) out.push(`Đợt cuối phải trong ${PLAN_MAX_MONTHS} tháng kể từ đợt 1.`);
  return out;
}
export const renumber = (steps: PayStep[]): PayStep[] => steps.map((s, i) => ({ ...s, no: i + 1 }));

export type PayStepSt = 'paid' | 'requested' | 'overdue' | 'due_soon' | 'upcoming' | 'open';
export const PAY_STEP_ST: Record<PayStepSt, { label: string; color: string }> = {
  paid: { label: 'Đã thu', color: 'green' },
  requested: { label: 'Chờ quầy thu', color: 'gold' },
  overdue: { label: 'Quá hạn', color: 'red' },
  due_soon: { label: 'Sắp đến hạn', color: 'orange' },
  upcoming: { label: 'Chưa đến hạn', color: 'default' },
  open: { label: 'Chưa hẹn ngày', color: 'default' },
};
/** days = Infinity khi đợt chưa hẹn ngày */
export interface PayRow extends PayStep { paid: number; requested?: SiteDeposit; state: PayStepSt; days: number }
/** Trạng thái từng đợt theo khoản quầy đã thu; sắp đến hạn = còn ≤ 3 ngày */
export function payRows(v: Pick<Visit, 'payPlan' | 'deposits'>, now: number): PayRow[] {
  return (v.payPlan ?? []).map((s) => {
    const ds = v.deposits.filter((d) => d.planNo === s.no);
    const paid = ds.filter((d) => d.status === 'received').reduce((x, d) => x + d.amount, 0);
    const requested = ds.find((d) => d.status === 'requested');
    const days = s.due == null ? Infinity : dayjs(s.due).startOf('day').diff(dayjs(now).startOf('day'), 'day');
    const state: PayStepSt = paid >= s.amount ? 'paid' : requested ? 'requested' : s.due == null ? 'open' : days < 0 ? 'overdue' : days <= 3 ? 'due_soon' : 'upcoming';
    return { ...s, paid, requested, state, days };
  });
}
export type SessionSt = 'scheduled' | 'planned' | 'unscheduled';
export const SESSION_ST: Record<SessionSt, { label: string; color: string }> = {
  scheduled: { label: 'Đã xếp', color: 'green' },
  planned: { label: 'Dự kiến', color: 'blue' },
  unscheduled: { label: 'Chưa xếp', color: 'default' },
};
export interface Session { no: number; at?: number; doctor?: string; room?: string; status: SessionSt }
/**
 * Một dịch vụ đã chuyển sang app thực hiện. lineId = dòng trên phiếu (hai dòng trùng tên vẫn chuyển riêng); lịch cũ chỉ có tên.
 * app = app chuyên môn nhận (Nội khoa / Ngoại khoa / Cấy tóc); note = dặn bộ phận thực hiện. Chuyển cả lượt thì các buổi để "Chưa xếp" — app thực hiện xếp lịch.
 */
export interface ExecPlan { id: string; at: number; by: string; line: string; lineId?: string; app?: 'noi' | 'ngoai' | 'toc'; note?: string; doctor: string; mode: 'all' | 'first'; gapDays: number; sessions: Session[]; cancelledAt?: number }

export type ResultKind = 'pay' | 'deposit' | 'think' | 'family' | 'price' | 'doctor' | 'no_need' | 'other';
export type FollowBy = 'Telesales' | 'Tư vấn cơ sở';
export const RESULTS: Record<ResultKind, { label: string; short: string; closed: boolean; callback?: boolean; follow?: boolean; tele: 'won' | 'deposit' | 'think' | 'no_need' | 'medical'; hint: string }> = {
  pay: { label: 'Chốt, thanh toán hôm nay', short: 'Chốt · thanh toán', closed: true, tele: 'won', hint: 'Mở POS với giỏ từ phiếu báo giá chính thức. Giữ mã S6 đến khi POS thanh toán, sau đó chuyển CS0.' },
  deposit: { label: 'Chốt, cọc giữ lịch', short: 'Chốt · cọc', closed: true, tele: 'deposit', hint: 'Cần có khoản thu ở Kế hoạch thanh toán — quầy POS thu, Tư vấn cơ sở không cầm tiền.' },
  think: { label: 'Cần suy nghĩ thêm, hẹn trả lời', short: 'Hẹn trả lời', closed: false, callback: true, follow: true, tele: 'think', hint: 'Ghi ngày khách hẹn trả lời và người chăm tiếp.' },
  family: { label: 'Chờ hỏi ý người thân', short: 'Chờ người thân', closed: false, callback: true, follow: true, tele: 'think', hint: 'Ghi người khách cần hỏi ý trong ghi chú.' },
  price: { label: 'Giá vượt dự tính', short: 'Giá vượt dự tính', closed: false, follow: true, tele: 'think', hint: 'Không giảm ngoài thẩm quyền; đề xuất phương án khác trong phiếu.' },
  doctor: { label: 'Bác sĩ chưa đồng ý làm', short: 'Bác sĩ chưa đồng ý', closed: false, tele: 'medical', hint: 'Ghi nhóm lý do, không ghi chẩn đoán (thuộc hồ sơ bệnh án).' },
  no_need: { label: 'Đến nhưng chưa có nhu cầu', short: 'Chưa có nhu cầu', closed: false, tele: 'no_need', hint: 'Khách đến tham khảo, chưa có nhu cầu làm.' },
  other: { label: 'Lý do khác', short: 'Lý do khác', closed: false, follow: true, tele: 'think', hint: 'Ghi rõ lý do trong ghi chú.' },
};
export interface Result { kind: ResultKind; note: string; callbackAt?: number; followBy?: FollowBy; amount?: number; at: number; by: string }
/** Mã S sau khi ghi kết quả (đặc tả Telesales: S7 chốt, S7.1 cọc, S4 trả về Telesales, S6.x rớt tại cơ sở) */
export const stageOf = (r: Pick<Result, 'kind' | 'followBy'>) => {
  if (r.kind === 'pay') return 'S7';
  if (r.kind === 'deposit') return 'S7.1';
  if (r.kind === 'no_need') return 'S6.1';
  if (r.kind === 'doctor') return 'S6.2';
  return r.followBy === 'Telesales' ? 'S4' : 'S6';
};

/** Dịch vụ trong danh mục API (giá niêm yết) */
export interface ApiSvc { id: number; name: string; price: number; qty: number; cat?: string }
/** gender theo quy ước chung với API: 1 Nam, 2 Nữ; 0, 3, null là chưa rõ hoặc Khác thì không có và xưng anh/chị */
export interface Cust { name: string; code: string; phone: string; gender?: 1 | 2 }
export const genderOf = (g: unknown): 1 | 2 | undefined => {
  const n = Number(g);
  return n === 1 || n === 2 ? n : undefined;
};
export interface Visit {
  id: string;
  code: string;
  branchId: string;
  createdAt: number;
  cust: Cust;
  mang: Mang;
  need: string;
  source: Source;
  /** Người phụ trách bên nguồn (Telesales / CSKH) */
  owner?: string;
  /** Trực page tạo khách / phụ trách chat */
  pageStaff?: string;
  /** Giờ hẹn trên lịch lúc check-in — so với lịch Telesales đặt để biết lịch đã dời */
  apptAt?: number;
  visitNo: number;
  flags?: string[];
  checkInAt?: number;
  /** Phiếu chuyển từ CSKH: hạn Tư vấn cơ sở nhận phiếu */
  referral?: { from: string; due: number; note: string };
  consultant?: string;
  assignedAt?: number;
  startedAt?: number;
  /** Phòng tư vấn: Lễ tân giữ trước khi phân, tư vấn viên nhận khách thì vào đúng phòng này */
  room?: string;
  /** Làm dịch vụ trong ngày: Lễ tân xếp phòng thủ thuật + kỹ thuật viên đang trống */
  exec?: { room: string; tech: string; doctor?: string; at: number; by: string; doneAt?: number };
  /**
   * Hàng chờ thực hiện: KTV / bác sĩ phụ trách khách (khách cũ, có lịch làm DV). notifiedAt = Lễ tân đã báo người phụ trách;
   * waitAt = khách chấp nhận chờ khi người phụ trách đang bận (không thì Lễ tân đổi người khác)
   */
  execOwner?: { tech?: string; doctor?: string; notifiedAt?: number; waitAt?: number };
  status: VisitSt;
  /** Phiếu nhu cầu Telesales ghi */
  needs?: { label: string; value: string }[];
  /** Khách tự khai báo thông tin ban đầu sau check-in (sàng lọc y khoa, ký tay) */
  screening?: Screening;
  online?: OnlineQuote;
  onlineDeps?: OnlineDeposit[];
  note: string;
  invite?: { doctor: string; at: number };
  quote?: OfficialQuote;
  deposits: SiteDeposit[];
  /** Kế hoạch thanh toán nhiều đợt theo phiếu báo giá chính thức */
  payPlan?: PayStep[];
  plans: ExecPlan[];
  result?: Result;
  /** Lượt tạo từ lịch hẹn Telesales — ghi ngược kết quả về hồ sơ khách */
  leadId?: number;
  apptId?: string;
  /** Lượt trên server (/reception/visits); chưa có thì lượt chỉ nằm trên máy, thao tác đầu tiên sẽ tạo trên server */
  serverId?: number;
  /** Hàng chờ do server xếp */
  queue?: 'consult' | 'exec';
  /** Đã khai báo y tế, phiếu có lưu ý y tế (chỉ để nhắc, không hiện tên bệnh ở danh sách) */
  hasDecl?: boolean;
  hasAlert?: boolean;
  /** Lượt tạo từ lịch hẹn trên API (khách check-in ở kiosk / Lễ tân) */
  apiApptId?: number;
  /** Dịch vụ khách đặt trên lịch hẹn API — phiếu báo giá lập từ đây */
  apptSvcs?: ApiSvc[];
  /** SĐT đầy đủ của khách chuyển từ quầy Trừ buổi — chống chuyển trùng */
  cardPhone?: string;
  /** SĐT đầy đủ, chỉ chữ số (cust.phone đã che nên không so được) — mọi nguồn đều ghi để chống tạo trùng lượt */
  phoneDigits?: string;
  log: LogItem[];
  seed?: string;
}

/** "Chị Lan" / "Anh Minh" — tên đã có sẵn danh xưng (Chị Demo, Anh Tuấn…) thì giữ nguyên, không thành "Chị Chị" */
export const custTitle = (c: Pick<Cust, 'name' | 'gender'>) => (/^(anh|chị|chi|em|cô|chú|bác|ông|bà)\s/i.test(c.name.trim()) ? c.name : `${c.gender === 1 ? 'Anh' : c.gender === 2 ? 'Chị' : 'Anh/Chị'} ${c.name}`);

export const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
export const vnd = (n?: number | null) => (n == null ? '—' : `${Math.round(n).toLocaleString('vi-VN')} đ`);
export const hm = (t?: number) => (t ? dayjs(t).format('HH:mm') : '—');
export const dmhm = (t?: number) => (t ? dayjs(t).format('HH:mm DD/MM') : '—');
export const minutes = (ms: number) => Math.max(0, Math.floor(ms / MIN));
export const durText = (ms: number) => {
  const m = minutes(ms);
  if (m < 60) return `${m} phút`;
  return `${Math.floor(m / 60)} giờ${m % 60 ? ` ${m % 60} phút` : ''}`;
};
/** Khách đang trong phòng thủ thuật: server không cho nhận tư vấn, đổi hàng chờ; lưu kết quả sẽ tự trả phòng */
export const inExecRoom = (v: Pick<Visit, 'exec'>) => !!v.exec && !v.exec.doneAt;
export const consultantsOf = (branchId: string) => CONSULTANTS.filter((c) => c.branchId === branchId);
export const branchName = (id?: string) => (id ? branchNames.get(id) : undefined) ?? BRANCHES.find((b) => b.id === id)?.name ?? '—';
export const isToday = (t: number | undefined, now: number) => !!t && dayjs(t).isSame(now, 'day');

/** Mã đánh số theo ngày: PREFIX-MMDD-NN */
export const nextCode = (prefix: string, used: string[], now: number) => {
  const d = `${prefix}-${dayjs(now).format('MMDD')}-`;
  const n = used.filter((c) => c.startsWith(d)).length + 1;
  return `${d}${String(n).padStart(2, '0')}`;
};
export const allCodes = (visits: Visit[]) => visits.flatMap((v) => [v.code, v.quote?.code ?? '', ...v.deposits.map((d) => d.code)]);

/* ---------------- Phiếu báo giá chính thức ---------------- */

export interface QuoteCalc { sub: number; base: number; disc: number; after: number; vat: number; total: number; cap: number }
export const lineTotal = (l: QLine) => (l.gift ? 0 : l.qty * l.price);
/** Tổng số buổi của dòng thẻ theo buổi (mỗi thẻ × số lượng thẻ); thẻ trả trước = 0 */
export const cardSessions = (l: Pick<QLine, 'card' | 'qty'>) => (l.card?.kind === 'session' ? l.card.items.reduce((s, x) => s + x.sessions, 0) * Math.max(1, l.qty) : 0);
export function calc(q: Pick<OfficialQuote, 'lines' | 'discount' | 'vat'>): QuoteCalc {
  const paid = q.lines.filter((l) => !l.gift);
  const sub = paid.reduce((s, l) => s + lineTotal(l), 0);
  // Giảm % chỉ áp lên dịch vụ không phải chủ lực; thẻ liệu trình bán theo giá gói (chiết khấu thẻ theo quy định gói ở POS)
  const open = paid.filter((l) => !l.core && !l.card);
  const base = open.reduce((s, l) => s + lineTotal(l), 0);
  const disc = Math.round((base * q.discount) / 100);
  const after = sub - disc;
  const vat = Math.round((after * q.vat) / 100);
  return { sub, base, disc, after, vat, total: after + vat, cap: open.length > 1 ? TRAN_COMBO : TRAN_DV };
}
export const needApprove = (q: Pick<OfficialQuote, 'discount'>) => q.discount > NGUONG_TVCS;

/** Lý do phiếu chưa có hiệu lực (rỗng = in, chuyển POS, chuyển thực hiện được) */
export function quoteIssues(q?: OfficialQuote): string[] {
  if (!q) return ['Chưa lập phiếu báo giá chính thức'];
  const c = calc(q);
  const out: string[] = [];
  if (!q.lines.length) out.push('Chưa có dịch vụ');
  if (q.lines.some((l) => !l.gift && (!l.qty || l.price <= 0))) out.push('Có dòng thiếu số lượng hoặc giá');
  if (q.discount > c.cap) out.push(`Giảm ${q.discount}% vượt trần ${c.cap}% — không xin duyệt được`);
  if (q.discount > 0 && !q.reason?.trim()) out.push('Thiếu lý do giảm');
  if (q.discount > 0 && !c.base) out.push('Phiếu chỉ có dịch vụ chủ lực / thẻ liệu trình — khoá giảm %');
  if (needApprove(q) && q.approval?.status !== 'approved') {
    out.push(q.approval?.status === 'pending' ? 'Chờ Trưởng phòng duyệt ưu đãi' : q.approval?.status === 'rejected' ? 'Trưởng phòng từ chối ưu đãi — sửa mức giảm' : 'Ưu đãi vượt quyền — cần xin duyệt');
  }
  if (!q.exam.signedAt) out.push('Chờ bác sĩ khám ký xác nhận');
  return out;
}
export const quoteValid = (q?: OfficialQuote) => quoteIssues(q).length === 0;

/**
 * Sửa dịch vụ / số lượng sau khi bác sĩ ký → mất chữ ký (bác sĩ ký trên dịch vụ, không thấy giá nên đổi ưu đãi, thuế không phải ký lại);
 * đổi mức giảm → mất duyệt; sửa gì cũng phải chuyển POS, gửi khách lại
 */
/** Dấu nhận biết bộ dịch vụ bác sĩ đã ký: dịch vụ + số lượng, không tính giá */
const svcKey = (lines: QLine[]) => lines.map((l) => `${l.svcId ?? l.name}×${l.qty}`).sort().join('|');

export function editQuote(q: OfficialQuote, p: Partial<OfficialQuote>): OfficialQuote {
  const content = 'lines' in p || 'discount' in p || 'program' in p || 'vat' in p;
  const next = { ...q, ...p };
  // Chỉ thêm / bớt dịch vụ hoặc đổi số lượng mới mất chữ ký; đổi giá, tặng kèm thì giữ (bác sĩ không thấy giá)
  if (p.lines && q.exam.signedAt && svcKey(p.lines) !== svcKey(q.lines)) next.exam = { ...next.exam, signedAt: undefined, sign: undefined };
  if ('discount' in p && p.discount !== q.discount) next.approval = undefined;
  if (content) { next.posAt = undefined; next.sentAt = undefined; }
  return next;
}

/** Tiền cọc đã nhận (online đã đối soát + tại cơ sở quầy đã thu) */
export const paidOf = (v: Visit) =>
  (v.onlineDeps ?? []).filter((d) => d.received).reduce((s, d) => s + d.amount, 0)
  + v.deposits.filter((d) => d.status === 'received').reduce((s, d) => s + d.amount, 0);

/**
 * So với báo giá online (R33): chênh lệch giá chốt so với giá "từ". So cùng loại: báo giá online không có thẻ liệu trình
 * thì bỏ thẻ bán thêm ở cơ sở ra khỏi phần so (noCards = true) — bán thêm thẻ không phải chênh giá
 */
export function vsOnline(v: Visit): { online: number; official: number; diff: number; pct: number; noCards: boolean } | null {
  if (!v.online?.total || !v.quote) return null;
  const noCards = !v.online.items?.some((x) => x.card) && v.quote.lines.some((l) => l.card);
  const official = calc(noCards ? { ...v.quote, lines: v.quote.lines.filter((l) => !l.card) } : v.quote).after;
  const diff = official - v.online.total;
  return { online: v.online.total, official, diff, pct: Math.round((diff / v.online.total) * 100), noCards };
}

/** Gợi ý cọc: 20% tổng phiếu, làm tròn 500.000, tối thiểu 1.000.000 */
export const suggestDeposit = (total: number) => Math.max(1_000_000, Math.round((total * 0.2) / 500_000) * 500_000);

/* ---------------- Chuyển thực hiện ---------------- */

export function buildSessions(count: number, first: number, gapDays: number, mode: 'all' | 'first', doctor: string, room: string): Session[] {
  return Array.from({ length: Math.max(1, count) }, (_, i) => {
    if (i === 0) return { no: 1, at: first, doctor, room, status: 'scheduled' as const };
    if (mode === 'first') return { no: i + 1, status: 'unscheduled' as const };
    return { no: i + 1, at: first + i * gapDays * DAY, doctor, status: 'planned' as const };
  });
}
export const activePlans = (v: Visit) => v.plans.filter((p) => !p.cancelledAt);

/* ---------------- Bước của lượt tư vấn ---------------- */

/** Thứ tự chốt ở buổi họp 30/10: tư vấn viên sàng lọc → bác sĩ khám (không thấy tiền) → báo giá → kế hoạch thanh toán → kết quả → chuyển thực hiện */
export type Step = 'consult' | 'exam' | 'quote' | 'deposit' | 'result' | 'exec';
export const STEPS: { key: Step; label: string; url: string }[] = [
  { key: 'consult', label: 'Phiếu tư vấn', url: '/phieu-tu-van' },
  { key: 'exam', label: 'Bác sĩ khám', url: '/bac-si-kham' },
  { key: 'quote', label: 'Báo giá chính thức', url: '/phieu-bao-gia' },
  { key: 'deposit', label: 'Kế hoạch thanh toán', url: '/ke-hoach-thanh-toan' },
  { key: 'result', label: 'Kết quả tư vấn', url: '/ket-qua-tu-van' },
  { key: 'exec', label: 'Chuyển thực hiện', url: '/chuyen-thuc-hien' },
];
export function stepDone(v: Visit, s: Step): boolean {
  switch (s) {
    case 'consult': return !!v.note.trim();
    case 'exam': return !!v.quote?.exam.signedAt;
    case 'quote': return quoteValid(v.quote);
    case 'deposit': return !!v.payPlan?.length || v.deposits.some((d) => d.status !== 'cancelled') || (v.onlineDeps ?? []).some((d) => d.received);
    case 'result': return !!v.result;
    case 'exec': return activePlans(v).length > 0;
    default: return false;
  }
}

/**
 * Bước khách đang ở (bước đầu chưa xong). Cọc là tuỳ chọn: đã ghi kết quả mà không cọc thì bỏ qua.
 * 'wait' = chưa có ai nhận khách; 'done' = xong cả 5 bước hoặc kết quả không chốt (không cần chuyển thực hiện).
 */
export type CurStep = Step | 'wait' | 'done';
export function curStep(v: Visit): CurStep {
  if (v.status === 'waiting' || v.status === 'referral') return 'wait';
  if (v.result && !RESULTS[v.result.kind].closed) return 'done';
  const s = STEPS.find((x) => !stepDone(v, x.key) && !(x.key === 'deposit' && v.result));
  return s ? s.key : 'done';
}

/** "Quá hạn 3 ngày" / "Còn 2 ngày" / "Đã thu"… */
export const payStepText = (r: PayRow) => (r.state === 'open' ? PAY_STEP_ST.open.label : r.state === 'overdue' ? `Quá hạn ${-r.days} ngày`
  : r.state === 'due_soon' ? (r.days === 0 ? 'Đến hạn hôm nay' : `Còn ${r.days} ngày`) : PAY_STEP_ST[r.state].label);

/* ---------------- Phân khách & thống kê ---------------- */

/** Tư vấn viên rảnh nhận khách tiếp theo: sẵn sàng, không đang tư vấn, ít lượt nhất trong ngày */
export function nextConsultant(visits: Visit[], branchId: string, staffSt: Record<string, StaffSt>, now: number, exclude?: string, pool: { id: string }[] = consultantsOf(branchId)): string | undefined {
  // Chỉ tính lượt hôm nay: lượt ngày trước quên đóng không được giữ chân tư vấn viên
  const busy = new Set(visits.filter((v) => isToday(v.checkInAt ?? v.createdAt, now) && (v.status === 'consulting' || (v.status === 'waiting' && v.consultant))).map((v) => v.consultant));
  const load = (id: string) => visits.filter((v) => v.consultant === id && isToday(v.assignedAt ?? v.startedAt, now)).length;
  return pool
    .filter((c) => c.id !== exclude && (staffSt[c.id] ?? 'ready') === 'ready' && !busy.has(c.id))
    .sort((a, b) => load(a.id) - load(b.id))[0]?.id;
}

/** Người được chia quá N phút chưa nhận khách thì chia lại cho người khác đang sẵn sàng */
export const REASSIGN_MIN = 10;
export type DispatchWhy = 'new' | 'late' | 'away';
export interface Dispatch { id: string; to: string; from?: string; why: DispatchWhy }
/**
 * Chia khách tự động: khách chờ chưa có người → tư vấn viên rảnh, ít lượt nhất;
 * người được chia đã tạm nghỉ / ngoại tuyến, hoặc quá REASSIGN_MIN phút chưa nhận → chia lại. Không có ai rảnh thì giữ nguyên.
 */
export function autoDispatch(visits: Visit[], branchId: string, staffSt: Record<string, StaffSt>, now: number, lateMin = REASSIGN_MIN, cons: { id: string }[] = consultantsOf(branchId)): Dispatch[] {
  let pool = visits.filter((v) => v.branchId === branchId);
  const out: Dispatch[] = [];
  pool.filter((v) => v.status === 'waiting' && !v.execOwner && !inExecRoom(v) && isToday(v.checkInAt, now))
    .sort((a, b) => (a.checkInAt ?? 0) - (b.checkInAt ?? 0))
    .forEach((v) => {
      const away = !!v.consultant && (staffSt[v.consultant] ?? 'ready') !== 'ready';
      const late = !!v.consultant && now - (v.assignedAt ?? now) >= lateMin * MIN;
      if (v.consultant && !away && !late) return;
      const others = pool.map((x) => (x.id === v.id ? { ...x, consultant: undefined } : x));
      const to = nextConsultant(others, branchId, staffSt, now, v.consultant, cons);
      if (!to) return;
      out.push({ id: v.id, to, from: v.consultant, why: !v.consultant ? 'new' : away ? 'away' : 'late' });
      pool = pool.map((x) => (x.id === v.id ? { ...x, consultant: to, assignedAt: now } : x));
    });
  return out;
}

export interface DayStats { consulted: number; closed: number; rate: number; revenue: number; deposit: number; reasons: Partial<Record<ResultKind, number>> }
export function dayStats(visits: Visit[], now: number, consultant?: string): DayStats {
  const done = visits.filter((v) => v.result && isToday(v.result.at, now) && (!consultant || v.consultant === consultant));
  const closed = done.filter((v) => RESULTS[v.result!.kind].closed);
  const reasons: Partial<Record<ResultKind, number>> = {};
  done.forEach((v) => { reasons[v.result!.kind] = (reasons[v.result!.kind] ?? 0) + 1; });
  return {
    consulted: done.length,
    closed: closed.length,
    rate: done.length ? Math.round((closed.length / done.length) * 100) : 0,
    revenue: closed.reduce((s, v) => s + (v.quote ? calc(v.quote).total : v.result!.amount ?? 0), 0),
    deposit: done.reduce((s, v) => s + paidOf(v), 0),
    reasons,
  };
}

/* ---------------- Tổng quan trong ngày (dashboard) ---------------- */

export interface Overview {
  checkIn: number;
  /** Phễu: check-in → được nhận → có ghi chép → bác sĩ khám → báo giá có hiệu lực → chốt → chuyển thực hiện */
  funnel: { key: string; label: string; n: number }[];
  bySource: { source: Source; n: number; done: number; closed: number }[];
  /** Khách check-in theo giờ 8h → 21h (21h gồm cả lượt muộn hơn) */
  byHour: { hour: number; n: number }[];
  /** Thời gian chờ trung bình đến khi được nhận (phút) */
  avgWait: number;
  /** Tiền quầy thu hôm nay (khoản thu tại cơ sở) */
  collected: number;
}
const SOURCES: Source[] = ['Telesales', 'Đặt lịch', 'Vãng lai', 'Khách cũ', 'CSKH'];
export function overviewOf(visits: Visit[], branchId: string, now: number): Overview {
  const today = visits.filter((v) => v.branchId === branchId && v.status !== 'referral' && v.status !== 'cancelled' && isToday(v.checkInAt ?? v.createdAt, now));
  const closed = (v: Visit) => !!v.result && RESULTS[v.result.kind].closed;
  const started = today.filter((v) => v.startedAt);
  const waits = started.filter((v) => v.checkInAt).map((v) => (v.startedAt! - v.checkInAt!) / 60_000);
  return {
    checkIn: today.length,
    funnel: [
      { key: 'in', label: 'Check-in', n: today.length },
      { key: 'start', label: 'Được nhận tư vấn', n: started.length },
      { key: 'consult', label: 'Có ghi chép tư vấn', n: today.filter((v) => stepDone(v, 'consult')).length },
      { key: 'exam', label: 'Bác sĩ đã khám, ký', n: today.filter((v) => stepDone(v, 'exam')).length },
      { key: 'quote', label: 'Báo giá có hiệu lực', n: today.filter((v) => stepDone(v, 'quote')).length },
      { key: 'closed', label: 'Chốt (thanh toán / cọc)', n: today.filter(closed).length },
      { key: 'exec', label: 'Đã chuyển thực hiện', n: today.filter((v) => stepDone(v, 'exec')).length },
    ],
    bySource: SOURCES.map((source) => {
      const xs = today.filter((v) => v.source === source);
      return { source, n: xs.length, done: xs.filter((v) => v.result).length, closed: xs.filter(closed).length };
    }).filter((x) => x.n),
    // 8h → 21h; check-in trước 8h vào cột 8h, sau 21h vào cột 21h để không sót khách
    byHour: Array.from({ length: 14 }, (_, i) => 8 + i).map((hour) => ({
      hour, n: today.filter((v) => v.checkInAt && Math.min(21, Math.max(8, dayjs(v.checkInAt).hour())) === hour).length,
    })),
    avgWait: waits.length ? Math.round(waits.reduce((s, x) => s + x, 0) / waits.length) : 0,
    collected: visits.filter((v) => v.branchId === branchId)
      .flatMap((v) => v.deposits).filter((d) => d.status === 'received' && isToday(d.receivedAt, now)).reduce((s, d) => s + d.amount, 0),
  };
}

/* ---------------- Dữ liệu mẫu theo ngày ---------------- */

/** Đổi số này khi đổi kịch bản dữ liệu mẫu — máy đang dùng tự nạp lại (giữ lượt người dùng tạo) */
export const SEED_VER = 7;

const NAMES: [string, 0 | 1][] = [
  ['Nguyễn Thị Lan', 0], ['Phạm Thu Quyên', 0], ['Quách Minh Hoàng', 1], ['Trần Kim Ngân', 0], ['Lê Đức Thịnh', 1],
  ['Hồ Thanh Vân', 0], ['Mai Ngọc Anh', 0], ['Đinh Quốc Toàn', 1], ['Vương Bảo Trâm', 0], ['Châu Gia Khang', 1],
  ['Tạ Mỹ Linh', 0], ['Lâm Hải Yến', 0], ['Kiều Văn Đạt', 1], ['Dương Thảo Nhi', 0], ['Phan Tuấn Kiệt', 1],
  ['Võ Ngọc Hân', 0], ['Trịnh Gia Bảo', 1], ['Lục Thanh Thảo', 0], ['Bạch Minh Tú', 0], ['Hà Quang Vinh', 1],
  ['Cao Thị Mỹ Duyên', 0], ['Tống Khánh Linh', 0], ['Đoàn Nhật Minh', 1], ['Ông Bích Ngọc', 0], ['La Hoài An', 0],
  ['Từ Thanh Phong', 1], ['Mạc Diệu Linh', 0], ['Giang Thùy Dung', 0], ['Lý Thành Đạt', 1], ['Huỳnh Mai Phương', 0],
];
const NEEDS: Record<Mang, string[]> = {
  Newhair: ['tóc thưa vùng đỉnh', 'rụng tóc sau sinh', 'hói chữ M', 'tóc mỏng, muốn phục hồi'],
  Clinic: ['nám, da không đều màu', 'chảy xệ vùng mặt', 'mụn viêm, thâm', 'da lão hoá, nếp nhăn'],
  PTTM: ['mũi thấp, muốn nâng', 'mí sụp', 'mỡ bụng sau sinh'],
  Partnership: ['khách đối tác giới thiệu'],
};
const WISH: Record<Mang, string> = {
  Newhair: 'Phục hồi, chưa muốn cấy', Clinic: 'Cải thiện rõ trong 1–2 tháng', PTTM: 'Dáng tự nhiên, ít nghỉ dưỡng', Partnership: 'Theo gói đối tác',
};
const SEED_MANG: Mang[] = ['Newhair', 'Clinic', 'Clinic', 'Newhair', 'PTTM', 'Clinic', 'Newhair'];
const TELE_OWNER = ['NV-01 Nguyễn Thu Hà', 'NV-02 Trần Minh Khoa', 'NV-03 Lê Ngọc Anh', 'NV-04 Phạm Quốc Bảo'];
const NOTE_BY: Partial<Record<ResultKind, string>> = {
  family: 'Chờ hỏi ý chồng', other: 'Khách đi công tác 2 tuần, hẹn quay lại', doctor: 'Cần điều trị nền trước 1 tháng', no_need: 'Đến tham khảo cùng bạn',
};

/** Dựng dòng phiếu mẫu cho một mảng */
export function sampleLines(mang: Mang, r: () => number): QLine[] {
  const cat = CATALOG[mang];
  const main = cat[mang === 'PTTM' ? 1 : 0];
  const lines: QLine[] = [{ id: uid(), svcId: main.id, name: main.name, unit: main.unit, qty: main.unit === 'buổi' ? 4 + Math.floor(r() * 5) : 1, price: main.price, core: main.core }];
  const extra = cat[cat.length - 1];
  if (r() < 0.7) lines.push({ id: uid(), svcId: extra.id, name: extra.name, unit: extra.unit, qty: 1, price: extra.price, core: extra.core });
  if (mang === 'Newhair') lines.push({ id: uid(), svcId: 'nh-check', name: 'Phân tích tóc định kỳ', unit: 'lần', qty: 1, price: 500_000, gift: true });
  return lines;
}

type SeedKind = 'done' | 'consulting' | 'waitAssigned' | 'waitNew' | 'referral';
interface SeedSpec {
  kind: SeedKind;
  /** Số phút trước lúc nạp (giờ check-in); khách đã tư vấn rải đều từ 8:00 đến lúc nạp */
  ago: number;
  result?: ResultKind;
  /** Đã tạo lịch thực hiện */
  plan?: boolean;
  /** Đang tư vấn: phiếu xin duyệt ưu đãi, chờ Trưởng phòng */
  approval?: boolean;
  /** Đang tư vấn: phiếu đã ký, cọc chờ quầy thu */
  depReq?: boolean;
  /** Đang tư vấn: đã mời bác sĩ, bác sĩ chưa khám */
  invite?: boolean;
  /** Đang tư vấn: vừa nhận khách, chưa ghi chép */
  fresh?: boolean;
  /** Phiếu CSKH quá hạn nhận */
  late?: boolean;
  source?: Source;
  /** Lượt của ngày trước (số ngày) — để có đợt trả góp quá hạn / đến hạn và khách hẹn trả lời hôm nay */
  daysAgo?: number;
  /** Kế hoạch trả 3 đợt, đợt 1 đã thu: đợt 2 quá hạn hoặc đến hạn hôm nay */
  payPlan?: 'overdue' | 'today';
  /** Khách hẹn trả lời trong hôm nay */
  callbackToday?: boolean;
}
/** Mốc xa nhất của khách hôm nay (phút) — dùng để rải giờ check-in vừa khung từ 8:00 */
const SEED_SPAN = 310;
/** Kịch bản mẫu theo cơ sở (thứ tự BRANCHES) — một cơ sở nên gom đủ mọi tình huống của Tổng quan / Cần chú ý */
const SPECS: SeedSpec[][] = [
  [
    { kind: 'done', ago: 300, result: 'pay', plan: true },
    { kind: 'done', ago: 280, result: 'deposit', plan: true },
    { kind: 'done', ago: 255, result: 'pay' },
    { kind: 'done', ago: 235, result: 'think' },
    { kind: 'done', ago: 215, result: 'price' },
    { kind: 'done', ago: 195, result: 'deposit', plan: true },
    { kind: 'done', ago: 180, result: 'no_need' },
    { kind: 'done', ago: 160, result: 'pay', plan: true },
    { kind: 'done', ago: 140, result: 'doctor' },
    { kind: 'done', ago: 120, result: 'family' },
    { kind: 'done', ago: 105, result: 'deposit', plan: true },
    { kind: 'done', ago: 90, result: 'other' },
    { kind: 'done', ago: 75, result: 'pay', plan: true },
    { kind: 'consulting', ago: 55, approval: true },
    { kind: 'consulting', ago: 45, depReq: true },
    { kind: 'consulting', ago: 35, invite: true },
    { kind: 'consulting', ago: 20, fresh: true },
    { kind: 'waitAssigned', ago: 9 },
    { kind: 'waitNew', ago: 24 },
    { kind: 'waitNew', ago: 12 },
    { kind: 'waitNew', ago: 5, source: 'Khách cũ' },
    { kind: 'referral', ago: 0, late: true },
    { kind: 'referral', ago: 0 },
    { kind: 'done', ago: 0, daysAgo: 35, result: 'deposit', plan: true, payPlan: 'overdue', source: 'Telesales' },
    { kind: 'done', ago: 0, daysAgo: 31, result: 'deposit', plan: true, payPlan: 'today', source: 'Khách cũ' },
    { kind: 'done', ago: 0, daysAgo: 2, result: 'think', callbackToday: true, source: 'Telesales' },
    { kind: 'done', ago: 0, daysAgo: 1, result: 'family', callbackToday: true },
  ],
];

/** Tên tư vấn viên theo mã (lịch sử ghi tên người) */
const nameOf = (id?: string) => CONSULTANTS.find((c) => c.id === id)?.name ?? id ?? '—';

export function seedVisits(now: number): Visit[] {
  const day = dayjs(now).format('YYYY-MM-DD');
  const r = rng(`tuvan-${day}`);
  const out: Visit[] = [];
  let k = 0;
  const codes: string[] = [];
  // Giờ check-in nằm trong giờ làm (từ 8:00): khách đã tư vấn rải theo cả ngày, khách đang chờ / đang tư vấn sát giờ nạp
  const span = Math.max(0, now - dayjs(now).startOf('day').hour(8).valueOf());
  const at = (sp: SeedSpec) => {
    if (sp.payPlan === 'today') return dayjs(now).subtract(1, 'month').hour(10).minute(15).startOf('minute').valueOf();
    if (sp.daysAgo) return dayjs(now).subtract(sp.daysAgo, 'day').hour(9 + (sp.daysAgo % 8)).minute(20).startOf('minute').valueOf();
    return now - Math.round(sp.kind === 'done' ? (span * sp.ago) / SEED_SPAN : Math.min(sp.ago * MIN, (span * sp.ago) / SEED_SPAN));
  };
  // Mốc sau check-in không vượt quá lúc nạp
  const past = (t: number) => Math.min(t, now - MIN);
  // Mở app sớm (ít phút từ 8:00): các khoảng trong một lượt (nhận khách, khám, ký, ghi kết quả) co theo tỉ lệ để không dồn cùng một phút
  const scale = Math.min(1, span / (SEED_SPAN * MIN));
  // Tư vấn viên: mỗi người đang tư vấn một khách, người kế tiếp được chia khách chờ, còn lại rảnh
  let busy = 0;
  const mk = (branchId: string, sp: SeedSpec): Visit => {
    const [name, gender] = NAMES[k % NAMES.length];
    const mang = SEED_MANG[k % SEED_MANG.length];
    k += 1;
    const { kind } = sp;
    const cons = consultantsOf(branchId);
    const source: Source = sp.source ?? (kind === 'referral' ? 'CSKH' : kind === 'waitNew' ? 'Vãng lai' : r() < 0.75 ? 'Telesales' : 'Khách cũ');
    const checkInAt = kind === 'referral' ? undefined : at(sp);
    const t0 = checkInAt ?? now;
    const d = (minutes: number) => Math.round(minutes * MIN * (sp.daysAgo ? 1 : scale));
    const code = nextCode('LV', codes, t0);
    codes.push(code);
    const v: Visit = {
      id: `seed-${day}-${k}`,
      seed: day,
      code,
      branchId,
      createdAt: checkInAt ?? now - (sp.late ? 200 : 90) * MIN,
      cust: { name, code: `KH-${30_000 + Math.floor(r() * 9000)}`, phone: `09${Math.floor(r() * 10)}***${String(1000 + Math.floor(r() * 9000))}`, gender: gender === 1 ? 1 : 2 },
      mang,
      need: NEEDS[mang][Math.floor(r() * NEEDS[mang].length)],
      source,
      owner: source === 'Telesales' ? TELE_OWNER[Math.floor(r() * TELE_OWNER.length)] : source === 'CSKH' ? 'NV-07 CSKH' : undefined,
      visitNo: source === 'Khách cũ' || source === 'CSKH' ? 2 : 1,
      checkInAt,
      status: kind === 'done' ? 'done' : kind === 'consulting' ? 'consulting' : kind === 'referral' ? 'referral' : 'waiting',
      note: '',
      deposits: [],
      plans: [],
      log: [],
    };
    if (kind === 'referral') {
      v.referral = sp.late
        ? { from: 'NV-07 CSKH', due: now - 25 * MIN, note: 'Khách hỏi gói duy trì sau liệu trình, muốn được tư vấn trực tiếp' }
        : { from: 'NV-07 CSKH', due: now + 2 * HOUR, note: 'Khách muốn thêm phác đồ sau 3 buổi, nhờ Tư vấn cơ sở gọi tư vấn và hẹn đến' };
    }
    if (source === 'Telesales') {
      const main = CATALOG[mang][0];
      const from = main.price * (main.unit === 'buổi' ? 4 : 1);
      v.needs = [
        { label: 'Vấn đề khách nêu', value: v.need },
        { label: 'Mong muốn', value: WISH[mang] },
        { label: 'Thời gian', value: 'Muốn bắt đầu trong tháng' },
        { label: 'Lưu ý', value: 'Hỏi kỹ số buổi, chi phí từng buổi' },
      ];
      v.online = { code: nextCode('BG', codes, t0 - 2 * DAY), sentAt: t0 - 2 * DAY, validUntil: t0 + 5 * DAY, items: [{ name: main.name, price: from }], total: from, by: v.owner! };
      codes.push(v.online.code);
      if (r() < 0.6) v.onlineDeps = [{ code: `CO-${dayjs(t0 - DAY).format('MMDD')}-${String(k).padStart(2, '0')}`, amount: 2_000_000, purpose: 'Giữ lịch', status: 'Đã nhận cọc', received: true }];
    }
    if (kind === 'consulting') { v.consultant = cons[busy % cons.length].id; busy += 1; }
    else if (kind === 'waitAssigned') v.consultant = cons[busy % cons.length].id;
    else if (kind === 'done') v.consultant = cons[k % cons.length].id;
    if (v.consultant) v.assignedAt = checkInAt! + d(3);
    const by = nameOf(v.consultant);
    if (kind === 'consulting' || kind === 'done') {
      v.startedAt = past(checkInAt! + d(6));
      v.room = CONSULT_ROOMS[(kind === 'consulting' ? busy - 1 : k) % CONSULT_ROOMS.length];
      if (!sp.fresh) v.note = `Vấn đề khách gặp: ${v.need}.\nGiải pháp / phác đồ đề xuất: ${CATALOG[mang][mang === 'PTTM' ? 1 : 0].name}.\nKhách cần cân nhắc: chi phí và thời gian nghỉ.`;
      if ((kind === 'consulting' && !sp.fresh) || (kind === 'done' && r() < 0.5)) v.invite = { doctor: DOCTORS[k % DOCTORS.length].id, at: past(v.startedAt + d(10)) };
    }
    // Có phiếu: khách đã tư vấn (trừ rớt do bác sĩ / chưa có nhu cầu) và khách đang tư vấn theo kịch bản phiếu
    if ((kind === 'done' && sp.result !== 'doctor' && sp.result !== 'no_need') || sp.approval || sp.depReq) {
      const lines = sampleLines(mang, r);
      const open = lines.some((l) => !l.core && !l.gift);
      const signed = Math.min(now - 2 * MIN, checkInAt! + d(40));
      const discount = sp.approval && open ? 15 : r() < 0.4 && open ? 5 : 0;
      const pending = !!sp.approval && discount > 0;
      const q: OfficialQuote = {
        code: nextCode('BG-CT', codes, t0), at: signed - d(10), by, lines, discount,
        reason: discount ? (sp.approval ? 'Khách giới thiệu thêm 2 người bạn' : 'Khách quay lại lần 2') : undefined, vat: 0,
        validUntil: Math.min(t0 + QUOTE_VALID_DAYS * DAY, quarterEnd(t0)),
        exam: { doctor: DOCTORS[k % DOCTORS.length].id, level: LEVELS[1 + (k % 2)], area: mang === 'Newhair' ? 'Đỉnh đầu' : mang === 'PTTM' ? 'Mắt' : 'Toàn mặt', signedAt: pending ? undefined : signed },
        ...(pending ? { approval: { status: 'pending' as const, pct: discount, at: now - 6 * MIN } } : {}),
      };
      v.quote = q;
      codes.push(q.code);
      const total = calc(q).total;
      if (sp.payPlan) {
        // Trả 3 đợt mỗi tháng: đợt 1 thu ngay hôm tư vấn, đợt 2 quá hạn / đến hạn hôm nay, đợt 3 tháng sau
        const first = Math.round((total * 0.4) / 500_000) * 500_000;
        v.payPlan = buildPayPlan(total, 3, first, t0, 1);
        const dc = nextCode('CO', codes, t0);
        codes.push(dc);
        v.deposits = [{ code: dc, amount: first, purpose: 'Thanh toán đợt 1', method: DEP_METHODS[0], status: 'received', at: signed + d(5), by, quote: q.code, receivedAt: signed + d(12), planNo: 1 }];
      } else if (sp.depReq || sp.result === 'deposit') {
        const dc = nextCode('CO', codes, t0);
        codes.push(dc);
        v.deposits = [{
          code: dc, amount: suggestDeposit(total), purpose: DEP_PURPOSES[k % DEP_PURPOSES.length], method: DEP_METHODS[k % DEP_METHODS.length],
          status: sp.depReq ? 'requested' : 'received', at: past(signed + d(5)), by, quote: q.code, ...(sp.depReq ? {} : { receivedAt: past(signed + d(12)) }),
        }];
      }
      if (sp.result === 'pay') { q.sentAt = past(signed + d(6)); q.posAt = past(signed + d(8)); }
      if (sp.result) {
        const kr = sp.result;
        v.result = {
          kind: kr, note: NOTE_BY[kr] ?? '', at: past(signed + d(15)), by,
          amount: kr === 'pay' ? total : kr === 'deposit' ? v.deposits[0]?.amount : undefined,
          ...(RESULTS[kr].callback ? { callbackAt: sp.callbackToday ? dayjs(now).hour(17).minute(0).startOf('minute').valueOf() : now + 2 * DAY } : {}),
          ...(RESULTS[kr].follow ? { followBy: (k % 2 ? 'Telesales' : 'Tư vấn cơ sở') as FollowBy } : {}),
        };
      }
      if (sp.plan) {
        const main = lines[0];
        const first = dayjs(sp.daysAgo ? t0 : now).add(1, 'day').hour(10).minute(0).second(0).millisecond(0).valueOf();
        const doctor = q.exam.doctor!;
        v.plans = [{
          id: `plan-${v.id}`, at: past((v.result?.at ?? now) + d(5)), by, line: main.name, doctor, mode: 'all', gapDays: 14,
          sessions: buildSessions(main.unit === 'buổi' ? main.qty : 1, first, 14, 'all', doctor, EXEC_ROOMS[k % EXEC_ROOMS.length]),
        }];
      }
    } else if (kind === 'done' && sp.result) {
      v.result = { kind: sp.result, note: NOTE_BY[sp.result] ?? '', at: past(checkInAt! + d(35)), by };
    }
    const q = v.quote;
    v.log = [
      ...(v.plans[0] ? [{ at: v.plans[0].at, text: `Tạo lịch thực hiện ${v.plans[0].line} · ${v.plans[0].sessions.length} buổi`, by }] : []),
      ...(v.result ? [{ at: v.result.at, text: `Ghi kết quả: ${RESULTS[v.result.kind].short} · mã ${stageOf(v.result)}`, by }] : []),
      ...(v.payPlan ? [{ at: v.deposits[0].at, text: `Lập kế hoạch thanh toán ${v.payPlan.length} đợt · quầy thu đợt 1 ${vnd(v.deposits[0].amount)}`, by }]
        : v.deposits[0] ? [{ at: v.deposits[0].at, text: `Gửi quầy thu cọc ${v.deposits[0].code} · ${vnd(v.deposits[0].amount)}`, by }] : []),
      ...(q?.approval ? [{ at: q.approval.at, text: `Xin Trưởng phòng duyệt giảm ${q.discount}%`, by }] : []),
      ...(q?.exam.signedAt ? [{ at: q.exam.signedAt, text: `${doctorName(q.exam.doctor)} ký xác nhận phiếu ${q.code}`, by: doctorName(q.exam.doctor) }] : []),
      ...(q ? [{ at: q.at, text: `Lập phiếu báo giá chính thức ${q.code}`, by }] : []),
      ...(v.invite ? [{ at: v.invite.at, text: `Mời ${doctorName(v.invite.doctor)} khám`, by }] : []),
      ...(v.startedAt ? [{ at: v.startedAt, text: `${by} nhận khách · ${v.room}`, by }] : []),
      ...(v.checkInAt ? [{ at: v.checkInAt, text: 'Check-in tại quầy lễ tân', by: 'Lễ tân' }] : [{ at: v.createdAt, text: 'CSKH chuyển phiếu sang Tư vấn cơ sở', by: 'NV-07 CSKH' }]),
    ].sort((a, b) => b.at - a.at);
    return v;
  };
  BRANCHES.forEach((b, i) => SPECS[Math.min(i, SPECS.length - 1)].forEach((sp) => out.push(mk(b.id, sp))));
  // Hàng chờ thực hiện của Lễ tân: khách cũ quay lại làm tiếp liệu trình, KTV phụ trách đang làm cho khách khác (báo đỏ)
  const busyBy = out.find((v) => v.result?.kind === 'pay' && v.plans.length && isToday(v.checkInAt, now));
  const back = out.find((v) => v.source === 'Khách cũ' && v.status === 'waiting');
  if (busyBy && back) {
    busyBy.exec = { room: 'Phòng laser', tech: 'KTV-01', at: now - 20 * MIN, by: 'Lễ tân' };
    back.need = 'Làm tiếp buổi 3 liệu trình';
    back.execOwner = { tech: 'KTV-01', doctor: DOCTORS[1].id };
  }
  return out;
}

/** Cuối quý của thời điểm t (hiệu lực phiếu không quá cuối quý) */
export const quarterEnd = (t: number) => {
  const d = dayjs(t);
  return d.date(1).month(Math.floor(d.month() / 3) * 3 + 2).endOf('month').valueOf();
};
